import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { ApiFilter, ApiPage } from "@result/rui/utils/api";

import ApiClient from "#/redux/api/ApiClient";
import { getErrorMessage } from "#/utils/APIUtils";

const baseUrl = "/api/dog/vnos/vnosUvozZivali";

const unwrapOutput = (data) =>
  data && typeof data === "object" && "output" in data ? data.output : data;

/**
 * Get import entry by entry ID
 */
export const getInsertByImportEventById = createAsyncThunk(
  "eventAnimalInsertImport/getById",
  async (eventId, { rejectWithValue, getState, fulfillWithValue }) => {
    try {
      const response = await ApiClient.get(`${baseUrl}/${eventId}`);
      return fulfillWithValue(unwrapOutput(response.data));
    } catch (error) {
      return rejectWithValue(error?.message || "Event not found");
    }
  }
);

/**
 * Find import entries (page)
 * GET /api/dog/vnos/vnosUvozZivali/page
 */
export const findInsertByImportEventPage = createAsyncThunk(
  "eventAnimalInsertImport/findPage",
  async (
    { page, filter, sort, stripQueryParams = false },
    { rejectWithValue }
  ) => {
    try {
      const response = await ApiClient.get(
        `${baseUrl}/page`,
        null,
        page,
        filter,
        sort,
        true,
        true
      );
      return response.data;
    } catch (error) {
      // axios abort/cancel
      if (error?.code === "ERR_CANCELED" || error?.name === "CanceledError") {
        return rejectWithValue(null); // or just return; (depends on your reducers)
      }
      return rejectWithValue(error?.message);
    }
  }
);

/**
 * Helper: find 1 import entry by zivalId (best-effort)
 */
export const findImportByZivalId = createAsyncThunk(
  "eventAnimalInsertImport/findByZivalId",
  async ({ zivalId }, { dispatch, rejectWithValue, fulfillWithValue }) => {
    try {
      if (!zivalId) return fulfillWithValue(null);

      const filter = new ApiFilter().where("zivalId", "EQ", zivalId);
      const page = new ApiPage({ pageNumber: 0, pageSize: 1 });

      const res = await dispatch(
        findInsertByImportEventPage({ page, filter, sort: null })
      ).unwrap();

      const content = res?.data?.content ?? res?.content ?? [];
      return fulfillWithValue(content?.[0] ?? null);
    } catch (e) {
      return rejectWithValue(e?.message);
    }
  }
);

/**
 * Save import entry
 * POST /api/dog/vnos/vnosUvozZivali
 * PUT  /api/dog/vnos/vnosUvozZivali/{id}
 */
export const saveAnimalInsertImportEvent = createAsyncThunk(
  "eventAnimalInsertImport/save",
  async (body, { rejectWithValue, fulfillWithValue }) => {
    try {
      if (!body) return rejectWithValue("No body provided");

      const isUpdate = !!body.id;
      const { id } = body;

      const response = isUpdate
        ? await ApiClient.put(`${baseUrl}/${id}`, body)
        : await ApiClient.post(`${baseUrl}`, body);

      return fulfillWithValue(unwrapOutput(response.data));
    } catch (error) {
      return rejectWithValue(error.response?.data);
    }
  }
);

export const findAnimalInsertImportEventByKey = createAsyncThunk(
  "animal/findAnimalInsertImportEvent",
  async (
    { key, page = null, filter = null, sort = null },
    { rejectWithValue }
  ) => {
    try {
      const response = await ApiClient.get(
        `${baseUrl}/page`,
        null,
        page instanceof ApiPage ? page.toQueryString() : page,
        filter,
        sort,
        false
      );
      return { key, data: response.data };
    } catch (error) {
      return rejectWithValue(getErrorMessage(error));
    }
  }
);
const initialState = {
  byId: {},
  page: {},
  isLoadingByKey: {},
  isLoadingPage: false,
  isLoading: false,
  errorPage: null,
  errorByKey: {},
  pageByKey: {},
};

const eventAnimalInsertImportSlice = createSlice({
  name: "eventAnimalInsertImport",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    // getById
    builder.addCase(getInsertByImportEventById.pending, (state, action) => {
      const id = action.meta.arg;
      state.byId[id] = state.byId[id] || {
        data: null,
        isLoading: false,
        error: null,
      };
      state.byId[id].isLoading = true;
      state.byId[id].error = null;
    });
    builder.addCase(getInsertByImportEventById.fulfilled, (state, action) => {
      const event = action.payload;
      const id = event?.id;
      if (!id) return;
      state.byId[id] = { data: event, isLoading: false, error: null };
    });
    builder.addCase(getInsertByImportEventById.rejected, (state, action) => {
      const id = action.meta.arg;
      state.byId[id] = state.byId[id] || {
        data: null,
        isLoading: false,
        error: null,
      };
      state.byId[id].isLoading = false;
      state.byId[id].error =
        action.payload || action.error?.message || "Load failed";
    });

    // page
    builder.addCase(findInsertByImportEventPage.pending, (state) => {
      state.isLoadingPage = true;
      state.isLoading = true;
      state.errorPage = null;
    });
    builder.addCase(findInsertByImportEventPage.fulfilled, (state, action) => {
      state.isLoadingPage = false;
      state.isLoading = false;
      state.page = action.payload;
    });
    builder.addCase(findInsertByImportEventPage.rejected, (state, action) => {
      state.isLoadingPage = false;
      state.isLoading = action.payload ? false : state.isLoading;
      state.errorPage =
        action.payload || action.error?.message || "Search failed";
    });
    builder
      .addCase(findAnimalInsertImportEventByKey.pending, (state, action) => {
        const { key } = action.meta.arg;
        state.isLoadingByKey[key] = true;
        state.errorByKey[key] = null;
      })
      .addCase(findAnimalInsertImportEventByKey.fulfilled, (state, action) => {
        const { key, data } = action.payload;
        state.isLoadingByKey[key] = false;
        state.pageByKey[key] = data || {};
      })
      .addCase(findAnimalInsertImportEventByKey.rejected, (state, action) => {
        const { key } = action.meta.arg;
        state.isLoadingByKey[key] = false;
        state.errorByKey[key] = action?.payload || action?.error?.message || {};
      });
    // save
    builder.addCase(saveAnimalInsertImportEvent.fulfilled, (state, action) => {
      const event = action.payload;
      const id = event?.id;
      if (!id) return;
      state.byId[id] = { data: event, isLoading: false, error: null };
    });
  },
});

export default eventAnimalInsertImportSlice.reducer;
